import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ButtonDropdown, Table, Tag } from '@bitcine/cinesend-theme'
import useStore from '../../hooks/use_store'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import useTableFilters from '../../hooks/use_table_filters'
import Tabs from '../layouts/tabs'
import uuid from 'react-uuid'
import { convertToShorthand } from '../../helpers/convert_date'
import { MISSING_UNIQUE_NAME } from '../../constants/category_display'

const Categories = ({ queryKey }) => {
  const params = useParams()
  const { deleteModel } = useStore()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const { data: airline, error: airlineError } = useQuery(['airlines/', params.airlineID])
  const { data: sections } = useQuery(['categories/', `?cycleID=${params.cycleID}&type=categories`])
  const langID = airline?.languages?.[0]?.id || null

  const selectedTab = searchParams.get('type') ?? 'categories'

  const sortingOptions = [
    selectedTab === 'assets' ? { label: 'Section', key: 'section_external_unique_name' } : null,
    { label: 'Unique Name', key: 'external_unique_name' },
    { label: 'Title', key: 'default_title' },
    selectedTab === 'assets' ? { label: 'Start Date', key: 'start_date' } : null,
    selectedTab === 'assets' ? { label: 'End Date', key: 'end_date' } : null,
  ].filter((item) => item !== null)

  let filteringOptions = null

  if (selectedTab === 'assets' && sections) {
    filteringOptions = [
      {
        label: 'Section',
        key: 'section_external_unique_name',
        type: 'checkboxes',
        options: sections?.map((section) => ({
          label: section.external_unique_name,
          key: section.external_unique_name,
        })),
      },
    ]
  }

  const { queryString, searching, sorting, filtering } = useTableFilters({
    filteringOptions,
    sortingOptions,
    enableSearch: true,
    queryExists: true,
    persistKey: `categories-table-${params.cycleID}`,
  })

  const queryWithTypeParam = `${queryString}&type=${selectedTab}`

  const { data: categories, isLoading, error } = useQuery([queryKey, queryWithTypeParam])

  const navigate = useNavigate()

  const typeToNameMapping = {
    categories: 'Section',
    assets: 'Asset Categories',
  }

  return (
    <>
      <Tabs
        className='mb-4'
        list={['categories', 'assets'].map((type) => {
          return {
            name: typeToNameMapping[type],
            selected: selectedTab === type,
            onClick: () => setSearchParams({ type }),
          }
        })}
      />
      <Table
        key={selectedTab}
        status={{
          pending: isLoading || !airline,
          error: error || airlineError,
          errorMessage: error?.message || airlineError?.message,
        }}
        widths={selectedTab === 'assets' ? [250, 300, 100, 600, 100, 100, 100, 65] : [300, 100, 600, 100, 65]}
        header={{
          columns: [
            selectedTab === 'assets' ? { text: 'Section (unique name)', key: 'section_external_unique_name' } : null,
            { text: 'Unique Name', key: 'external_unique_name' },
            { text: 'Title', key: 'default_title' },
            { text: 'Items' },
            selectedTab === 'assets' ? { text: 'Start', key: 'start_date' } : null,
            selectedTab === 'assets' ? { text: 'End', key: 'end_date' } : null,
            { text: 'Count' },
            { text: '' },
          ].filter((item) => item !== null),
          searching,
          sorting,
          filtering,
        }}
        body={{
          data: categories,
          row: {
            compact: true,
            spaced: true,
            langID: langID,
            onClick: (event, data) => navigate(`${data.id}/items`),
            render: [
              selectedTab === 'assets'
                ? (data) => <div className='truncate'>{data.section_external_unique_name ?? ''}</div>
                : null,
              (data) => (
                <div className='truncate'>
                  {data.external_unique_name || (selectedTab === 'assets' ? MISSING_UNIQUE_NAME : '')}
                </div>
              ),
              (data) => data.default_title || '',
              (data, _, { langID }) => (
                <div className='flex flex-wrap gap-2'>
                  {[
                    ...data.category_items,
                    ...data.child_categories.map((item) => item.category_values.find((v) => v.language_id === langID)),
                  ].map((item, index) => (
                    <Tag
                      key={`${data.id}-${item?.title || item?.value}-${index}`}
                      outline
                      label={item?.title || item?.value}
                    />
                  ))}
                </div>
              ),
              selectedTab === 'assets' ? (data) => convertToShorthand(data.start_date) : null,
              selectedTab === 'assets' ? (data) => convertToShorthand(data.end_date) : null,
              (data) => (selectedTab === 'categories' ? data.child_categories.length : data.category_items.length),
              (data) => (
                <ButtonDropdown
                  kebab
                  dropdown={{
                    content: [
                      {
                        text: 'Delete',
                        className: 'text-red-500',
                        onClick: () =>
                          deleteModel.mutate(
                            {
                              endpoint: 'categories',
                              id: data.id,
                            },
                            {
                              onSuccess: () => {
                                queryClient.invalidateQueries([queryKey])
                              },
                            }
                          ),
                      },
                    ],
                  }}
                />
              ),
            ].filter((item) => item !== null),
          },
          empty: {
            title: `No ${typeToNameMapping[selectedTab]} found!`,
            text: `Create a new ${typeToNameMapping[selectedTab]} to begin.`,
            icon: 'category',
          },
        }}
      />
    </>
  )
}

export default Categories
